import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { CenterAlignedCell } from 'components/Table';
import BidEntryDto from 'types/api/BidEntryDto';
import FormattedNumberCell from 'components/Table/components/FormattedNumberCell/FormattedNumberCell';
import DollarValueCell from 'components/Table/components/DollarValueCell/DollarValueCell';
import BooleanValueCell from 'components/Table/components/BooleanCell/BooleanValueCell';
import { DEFAULT_DATE_TIME_FORMAT, formatDate } from 'hooks/useFormattedDate';
import useUser from 'hooks/useUser';

const useColumns = () => {
  const { t } = useTranslation();
  const { isReviewer } = useUser();

  return useMemo(() => {
    const columnHelper = createColumnHelper<BidEntryDto>();

    const columns: ColumnDef<BidEntryDto, any>[] = [];

    if (isReviewer) {
      columns.push(
        columnHelper.accessor('organization', {
          cell: CenterAlignedCell,
          header: t('pages.prosforaElement.bidHistory.organization'),
          size: 10,
          meta: {
            headerClassName: 'center',
          },
        }),
      );
    } else {
      columns.push(
        columnHelper.accessor('my_bid', {
          cell: BooleanValueCell,
          header: t('pages.prosforaElement.bidHistory.myBid'),
          size: 10,
          meta: {
            headerClassName: 'center',
          },
        }),
      );
    }

    columns.push(
      columnHelper.accessor('volume', {
        cell: FormattedNumberCell,
        header: t('pages.prosforaElement.bidHistory.volume'),
        size: 10,
        meta: {
          headerClassName: 'right',
        },
      }),
      columnHelper.accessor('bid_price', {
        cell: DollarValueCell,
        header: t('pages.prosforaElement.bidHistory.bidPrice'),
        size: 10,
        meta: {
          headerClassName: 'right',
        },
      }),
      columnHelper.accessor('sub_total', {
        cell: DollarValueCell,
        header: t('pages.prosforaElement.bidHistory.subTotal'),
        size: 10,
        meta: {
          headerClassName: 'right',
        },
      }),
      columnHelper.accessor('down_payment', {
        cell: DollarValueCell,
        header: t('pages.prosforaElement.bidHistory.downPayment'),
        size: 10,
        meta: {
          headerClassName: 'right',
        },
      }),
      columnHelper.accessor<(row: BidEntryDto, index: number) => string, string>((row): string => `${row.discount} %`, {
        id: 'discount',
        cell: CenterAlignedCell,
        header: t('pages.prosforaElement.bidHistory.discount'),
        size: 10,
        meta: {
          headerClassName: 'center',
        },
      }),
      columnHelper.accessor('total', {
        cell: DollarValueCell,
        header: t('pages.prosforaElement.bidHistory.total'),
        size: 10,
        meta: {
          headerClassName: 'right',
        },
      }),
      columnHelper.accessor('average_price', {
        cell: DollarValueCell,
        header: t('pages.prosforaElement.bidHistory.avgPrice'),
        size: 10,
        meta: {
          headerClassName: 'right',
        },
      }),
    );

    if (isReviewer) {
      columns.push(
        columnHelper.accessor((row) => formatDate(row.bid_date, DEFAULT_DATE_TIME_FORMAT), {
          id: 'bid_date',
          header: t('pages.prosforaElement.bidHistory.bidDate'),
          cell: CenterAlignedCell,
          size: 50,
          meta: {
            headerClassName: 'center',
          },
        }),
      );
    }

    return columns;
  }, [t, isReviewer]);
};

export default useColumns;
